import type { User, UserRole } from '../../types';
import { apiClient } from './client';
import type { StaffRole } from './platform.service';

type MaybeWrapped<T> = T | { data: T };

type UserListResponse =
  | User[]
  | {
      data?: User[];
      items?: User[];
      total?: number;
      meta?: {
        total?: number;
      };
    };

type UserActivityResponse =
  | {
      data?: UserActivity[];
      total?: number;
      meta?: {
        total?: number;
      };
    }
  | UserActivity[];

function unwrapData<T>(payload: MaybeWrapped<T>): T {
  if (payload && typeof payload === 'object' && !Array.isArray(payload) && 'data' in payload) {
    return payload.data;
  }

  return payload as T;
}

function normalizeUserListResponse(payload: UserListResponse): { data: User[]; total: number } {
  if (Array.isArray(payload)) {
    return { data: payload, total: payload.length };
  }

  const data = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload?.data) ? payload.data : [];
  const total = payload?.meta?.total ?? payload?.total ?? data.length;

  return { data, total };
}

function normalizeUserActivityResponse(payload: UserActivityResponse): { data: UserActivity[]; total: number } {
  if (Array.isArray(payload)) {
    return { data: payload, total: payload.length };
  }

  const data = Array.isArray(payload?.data) ? payload.data : [];
  const total = payload?.meta?.total ?? payload?.total ?? data.length;

  return { data, total };
}

/** A user as the Users page lists them: their staff role, if any, and whether invited, active or removed. */
export type ListedUser = User & {
  staffRole?: StaffRole | null;
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'LOCKED';
};

/** A role is a system `role` or a built-in `staffRole`. Without `password`, the invitation is emailed. */
export interface InviteUserDto {
  email: string;
  role?: UserRole;
  staffRole?: StaffRole;
  firstName?: string;
  lastName?: string;
  customRoleId?: string | null;
  password?: string;
}

export interface UserReportingScope {
  companyMode: 'TENANT' | 'RESTRICTED';
  branchMode: 'TENANT' | 'RESTRICTED';
  companyIds: number[];
  branchIds: string[];
  assigned?: boolean;
}

export interface ReportingScopeOptions {
  companies: Array<{ id: number; name: string }>;
  branches: Array<{ id: string; name: string; companyIds: number[] }>;
}

export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  staffRole?: StaffRole;
  customRoleId?: string | null;
  isActive?: boolean;
}

export interface UpdateProfileDto {
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
}

export interface UserActivity {
  id: string;
  action: string;
  resource: string;
  resourceId: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export const userService = {
  async reportingScopeOptions(): Promise<ReportingScopeOptions> {
    const response = await apiClient.get<MaybeWrapped<ReportingScopeOptions>>('/users/reporting-access/options');
    return unwrapData(response.data);
  },

  async getReportingScope(id: string): Promise<UserReportingScope> {
    const response = await apiClient.get<MaybeWrapped<UserReportingScope>>(`/users/${id}/reporting-access`);
    return unwrapData(response.data);
  },

  async updateReportingScope(id: string, dto: UserReportingScope): Promise<UserReportingScope> {
    const { companyMode, branchMode, companyIds, branchIds } = dto;
    const response = await apiClient.patch<MaybeWrapped<UserReportingScope>>(`/users/${id}/reporting-access`, {
      companyMode,
      branchMode,
      companyIds,
      branchIds,
    });
    return unwrapData(response.data);
  },
  async getAll(params?: {
    page?: number;
    limit?: number;
    pageSize?: number;
    search?: string;
    role?: string;
    status?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    filters?: string;
  }): Promise<{ data: User[]; total: number }> {
    const response = await apiClient.get<UserListResponse>('/users', { params });
    return normalizeUserListResponse(response.data);
  },

  async getById(id: string): Promise<User> {
    const response = await apiClient.get<MaybeWrapped<User>>(`/users/${id}`);
    return unwrapData(response.data);
  },

  /** Whether the server can email invitations; without it the inviter sets a temporary password. */
  async inviteOptions(): Promise<{ email: boolean }> {
    const response = await apiClient.get<{ email: boolean }>('/users/invite-options');
    return response.data;
  },

  async invite(dto: InviteUserDto): Promise<User> {
    const response = await apiClient.post<MaybeWrapped<User>>('/users/invite', dto);
    return unwrapData(response.data);
  },

  async update(id: string, dto: UpdateUserDto): Promise<User> {
    const response = await apiClient.patch<MaybeWrapped<User>>(`/users/${id}`, dto);
    return unwrapData(response.data);
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  async activate(id: string): Promise<User> {
    const response = await apiClient.post<MaybeWrapped<User>>(`/users/${id}/activate`);
    return unwrapData(response.data);
  },

  async deactivate(id: string): Promise<User> {
    const response = await apiClient.post<MaybeWrapped<User>>(`/users/${id}/deactivate`);
    return unwrapData(response.data);
  },

  async resendInvite(id: string): Promise<void> {
    await apiClient.post(`/users/${id}/resend-invite`);
  },

  async getProfile(): Promise<User> {
    const response = await apiClient.get<MaybeWrapped<User>>('/users/me');
    return unwrapData(response.data);
  },

  async updateProfile(dto: UpdateProfileDto): Promise<User> {
    const response = await apiClient.patch<MaybeWrapped<User>>('/users/me', dto);
    return unwrapData(response.data);
  },

  async changePassword(dto: ChangePasswordDto): Promise<void> {
    await apiClient.post('/users/profile/change-password', dto);
  },

  async getActivity(
    userId: string,
    params?: { page?: number; limit?: number },
  ): Promise<{ data: UserActivity[]; total: number }> {
    const response = await apiClient.get<UserActivityResponse>(`/users/${userId}/activity`, { params });
    return normalizeUserActivityResponse(response.data);
  },

  async uploadAvatar(avatarUrl?: string): Promise<{ url: string }> {
    const { data } = await apiClient.post<{ data: { url: string } }>('/users/profile/avatar', {
      avatarUrl,
    });
    return data.data;
  },

  async searchUsers(params?: { search?: string }): Promise<ListedUser[]> {
    const response = await apiClient.get<UserListResponse>('/users', { params });
    return normalizeUserListResponse(response.data).data;
  },
};
